import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { styled } from "@mui/system";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  Avatar as MuiAvatar,
  Button as MuiButton,
  Chip as MuiChip,
  CircularProgress,
  Dialog,
  IconButton as MuiIconButton,
  Menu,
  MenuItem,
  Popover,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import extensibilitySdk from "@outreach/extensibility-sdk";
import Select from "../../components/DataInput/Select";
import { ensureSignedInFromExtension, resolveLinkedInUrl } from "../../services/extension";
import {
  getLinkedInConversations,
  getLinkedInInbox,
  getLinkedInThread,
  removeLinkedInConversation,
  searchGiphy,
  sendLinkedInReply,
  uploadLinkedInAttachment,
} from "../../services/firebase";
import type { GifResult, LinkedInAttachment } from "../../services/firebase";
import { InboxConversation, InboxMedia, InboxMessage } from "../../types/addons";
import { isPlanRequiredError, planRequiredMessage } from "../../common/plans";

/** Plan gating: LinkedIn DMs in the CRM are Prospector for organizations created after it. */
const PLAN_NOTE = `${planRequiredMessage("linkedinInbox")}. An admin can change the plan in boring under Settings, Billing.`;

/**
 * The Outreach surfaces for LinkedIn conversations.
 *
 * As a prospect TAB this is a table of every conversation the team has had
 * with the person: who contacted them, the last message, what happened, and
 * when. A row opens the thread. As a prospect TILE it opens straight into the
 * newest thread. As the application INBOX it is the same table across the
 * whole team, filterable by rep.
 *
 * It sits inside Outreach's page, so it is built from the same parts Outreach
 * is built from (MUI) and painted with the values measured off their UI:
 * Roboto Flex 14px in #454262, muted #686783, brand #5E5EAF, 1px #E8EAF1
 * rules, 36px pill buttons, 36px round icon buttons, 64px table rows.
 */

const INK = "#454262";
/* Outreach's destructive red. */
const DANGER = "#C0364A";
const MUTED = "#686783";
const BRAND = "#5E5EAF";
const BRAND_HOVER = "#4B4B9E";
const SOFT = "#E8EAF1";
const SOFT_HOVER = "#DCDEEA";
const RULE = "#E8EAF1";
const WASH = "rgba(94, 94, 175, 0.06)";
const AVATAR = "#5A5B7C";
const OFF = "#C9CAD6";
const CANVAS = "#fefdfc"; // measured: the panel Outreach paints behind the frame
const OUTREACH_SANS = '"Roboto Flex", Roboto, "Helvetica Neue", Arial, sans-serif';
const PANEL_SHADOW = "rgba(69, 66, 98, 0.16) 0 0 1px, rgba(69, 66, 98, 0.16) 0 1px 2px";

/* Outreach is light. The dashboard may be in dark mode, and MUI's popovers,
   tooltips and skeletons follow the theme, so the embedded pages pin light. */
const outreachTheme = createTheme({
  palette: { mode: "light", primary: { main: BRAND }, text: { primary: INK, secondary: MUTED } },
  typography: { fontFamily: OUTREACH_SANS },
  shape: { borderRadius: 8 },
});

const Page = styled("div")`
  font-family: ${OUTREACH_SANS};
  /* Outreach draws its text antialiased; without this the same Roboto Flex
     reads heavier here than in Outreach's own tabs. */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-size: 14px;
  line-height: 20px;
  color: ${INK};
  background: ${CANVAS};
  height: 100vh;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

/* ── Outreach's controls ── */

const PrimaryButton = styled(MuiButton)`
  height: 36px;
  min-width: 64px;
  padding: 0 12px;
  border-radius: 100px;
  background: ${BRAND};
  color: #fff;
  font: 600 14px/1 ${OUTREACH_SANS};
  text-transform: none;
  box-shadow: none;
  gap: 8px;

  &:hover {
    background: ${BRAND_HOVER};
    box-shadow: none;
  }

  &.Mui-disabled {
    background: ${SOFT};
    color: #a3a2bd;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const SecondaryButton = styled(MuiButton)`
  height: 36px;
  min-width: 64px;
  padding: 0 12px;
  border-radius: 100px;
  background: ${SOFT};
  color: ${BRAND};
  font: 600 14px/1 ${OUTREACH_SANS};
  text-transform: none;
  box-shadow: none;
  gap: 8px;

  &:hover {
    background: ${SOFT_HOVER};
    box-shadow: none;
  }

  &.Mui-disabled {
    background: ${SOFT};
    color: #a3a2bd;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

/* Outreach's round icon buttons hover to a faint grey, not the filled SOFT. */
const ICON_HOVER = "#F2F2F5";
const RoundIcon = styled(MuiIconButton)`
  width: 36px;
  height: 36px;
  color: ${MUTED};

  &:hover {
    background: ${ICON_HOVER};
    color: ${INK};
  }

  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
`;

/* Measured off Outreach's own loading table: MUI text skeleton, 11% ink. */
const Bone = styled(Skeleton)`
  background-color: rgba(69, 66, 98, 0.11) !important;
`;

/* The extension's own tooltip on the LinkedIn button it injects into Outreach
   (sms-tooltip in linkedin/styles.css): #4D4B6C, white 500 14px, 8px 16px, 6px corners. */
const TIP_SX = {
  background: "#4d4b6c",
  color: "#ffffff",
  fontFamily: OUTREACH_SANS,
  fontSize: "14px",
  fontWeight: 500,
  lineHeight: "20px",
  padding: "8px 16px",
  borderRadius: "6px",
  boxShadow: "none",
  maxWidth: 280,
};
const Tip = (props: React.ComponentProps<typeof Tooltip>) => (
  <Tooltip
    {...props}
    arrow
    enterDelay={300}
    slotProps={{ tooltip: { sx: TIP_SX, className: "boring-outreach-tip" }, arrow: { sx: { color: "#4d4b6c" } } }}
    PopperProps={{ className: "boring-outreach-popper", sx: { "& .MuiTooltip-tooltip": TIP_SX, "& .MuiTooltip-arrow": { color: "#4d4b6c" } } }}
  />
);

const Face = styled(MuiAvatar)`
  width: 36px;
  height: 36px;
  background: ${AVATAR};
  color: #fff;
  font: 600 13px/1 ${OUTREACH_SANS};
`;

/* ── table ── */

const Panel = styled("div")`
  background: transparent;
  padding-top: 0;
  overflow: hidden;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Scrollable = styled("div")`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

/* The gutter Outreach's own tabs (Emails) keep around their table, measured:
   24px above, 12px each side. */
const TablePanel = styled(Panel)`
  padding: 24px 12px 0;
`;

const HeadCell = styled(TableCell)`
  height: 48px;
  padding: 0 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  background: ${CANVAS};
  font: 600 13px/16px ${OUTREACH_SANS};
  color: ${MUTED};
  white-space: nowrap;
`;

const Cell = styled(TableCell)`
  height: 64px;
  padding: 8px 12px;
  border-bottom: 1px solid ${RULE};
  font: 400 14px/20px ${OUTREACH_SANS};
  color: ${INK};
`;

/* Measured off Outreach: a 52px band, subtitle2 in muted ink with a 20px glass. */
const NoResults = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 0;
  font: 600 13px/16px ${OUTREACH_SANS};
  color: ${MUTED};

  svg {
    width: 20px;
    height: 20px;
    fill: ${MUTED};
  }
`;

const SearchIcon = () => (
  <svg viewBox="0 0 24 24">
    <path fillRule="evenodd" clipRule="evenodd" d="M10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C11.8487 18 13.551 17.3729 14.9056 16.3199L19.2928 20.7071C19.6833 21.0977 20.3165 21.0977 20.707 20.7071C21.0975 20.3166 21.0975 19.6835 20.707 19.2929L16.3199 14.9057C17.3729 13.551 18 11.8487 18 10C18 5.58172 14.4183 2 10 2ZM4 10C4 6.68629 6.68629 4 10 4C13.3137 4 16 6.68629 16 10C16 13.3137 13.3137 16 10 16C6.68629 16 4 13.3137 4 10Z" />
  </svg>
);

const ClickRow = styled(TableRow)`
  cursor: pointer;

  &:hover {
    background: rgba(69, 66, 98, 0.04);
  }

  &:last-of-type td {
    border-bottom: none;
  }
`;

const Person = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const Name = styled("span")`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${INK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Snippet = styled("div")`
  font: 400 14px/20px ${OUTREACH_SANS};
  color: ${INK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const DateCell = styled("span")`
  color: ${INK};
  white-space: nowrap;
`;

const Engagement = styled("div")`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Icon = styled("span")<{ on?: boolean }>`
  position: relative;
  display: inline-flex;
  color: ${({ on }) => (on ? BRAND : "rgba(163, 162, 189, 0.38)")};

  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
  }
`;

const Count = styled("span")`
  position: absolute;
  top: -6px;
  left: 16px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: ${SOFT};
  color: ${INK};
  font: 500 10px/1 ${OUTREACH_SANS};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
`;

const Centered = styled("div")`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
`;

const Empty = styled("div")`
  padding: 24px;
  color: ${MUTED};
`;

const Filter = styled("div")`
  padding: 8px 0 12px;
  width: 240px;
`;

/* ── thread ── */

const ThreadPanel = styled(Panel)`
  padding: 12px 8px 16px;
`;

const ThreadHead = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px 8px;
`;

const Scroll = styled("div")`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 16px;
`;

const DayRule = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 12px 0 24px;
  font-size: 13px;
  color: ${INK};

  &::before,
  &::after {
    content: "";
    flex: 1;
    border-top: 1px solid ${RULE};
  }

  span {
    color: ${MUTED};
  }
`;

const Message = styled("div")`
  position: relative;
  display: grid;
  grid-template-columns: 36px 1fr;
  column-gap: 14px;
  margin: 0 -10px 4px;
  padding: 10px;
  border-radius: 12px;

  &:hover {
    background: #f7f7fa;
  }

  &:hover .msg-actions {
    opacity: 1;
    pointer-events: auto;
  }
`;

/* React and Reply show on hover, at the right of the message. */
const MessageActions = styled("div")`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 4px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
  z-index: 2;
`;

const SmallIcon = styled(RoundIcon)`
  width: 28px;
  height: 28px;
  border: 1px solid ${RULE};
  border-radius: 8px;
  background: #fff;

  &:hover {
    background: #eceef3;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const Quote = styled("div")`
  margin: 0 0 8px;
  padding: 6px 10px;
  border-left: 2px solid ${BRAND};
  color: ${MUTED};
  font-size: 13px;
  line-height: 18px;
`;

const Chips = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
`;

const ReactionChip = styled(MuiChip)<{ on?: boolean }>`
  height: 24px;
  border-radius: 12px;
  background: ${({ on }) => (on ? "rgba(94, 94, 175, 0.12)" : SOFT)};
  color: ${INK};
  font: 400 12px/1 ${OUTREACH_SANS};

  .MuiChip-label {
    padding: 0 8px;
  }
`;

const EmojiSheet = styled("div")`
  position: absolute;
  top: 40px;
  right: 8px;
  z-index: 5;
  display: flex;
  gap: 2px;
  padding: 4px;
  background: #fff;
  border-radius: 100px;
  box-shadow: ${PANEL_SHADOW}, 0 8px 24px rgba(69, 66, 98, 0.12);
`;

const EmojiBtn = styled("button")`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  font-size: 16px;

  &:hover {
    background: ${SOFT};
  }
`;

const Sender = styled("div")`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin: 8px 0 6px;
  font-weight: 600;
  color: ${INK};

  small {
    font-size: 12px;
    font-weight: 400;
    color: ${MUTED};
  }
`;

const Body = styled("div")`
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 22px;
`;

const Deleted = styled(Body)`
  font-style: italic;
  color: ${MUTED};
`;

const MediaStack = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
  max-width: 360px;
`;

const MediaFrame = styled("div")`
  overflow: hidden;
  border-radius: 8px;
  box-shadow: ${PANEL_SHADOW};
  background: #f8f8fa;
  max-width: 100%;

  img,
  video {
    display: block;
    max-width: 100%;
    max-height: 280px;
    cursor: pointer;
  }

  audio {
    display: block;
    width: 280px;
    margin: 8px;
  }
`;

const FileChip = styled("a")`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 180px;
  max-width: 360px;
  padding: 8px 10px;
  border-radius: 8px;
  box-shadow: ${PANEL_SHADOW};
  background: #fff;
  color: inherit;
  text-decoration: none;

  &:hover {
    background: ${WASH};
  }
`;

const FileIcon = styled("span")`
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${SOFT};
  color: ${BRAND};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font: 700 10px/1 ${OUTREACH_SANS};
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const FileMeta = styled("span")`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-size: 13px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    color: ${MUTED};
    font-size: 11px;
  }
`;

/* The reply box, as the LinkedIn modal draws it: white, a thin brand-blue
   border, tools along its bottom edge. */
const Composer = styled("div")`
  position: relative;
  margin: 8px 24px 0;
  background: #fff;
  border: 1px solid ${BRAND};
  border-radius: 8px;
  padding: 16px 20px 12px;
  transition: box-shadow 0.15s ease;

  &:focus-within {
    box-shadow: 0 0 0 1px ${BRAND};
  }
`;

const ReplyBar = styled("div")`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 8px;
  padding: 8px 10px;
  background: #fff;
  border-radius: 8px;
  border-left: 2px solid ${BRAND};
  font-size: 13px;
  line-height: 18px;
  color: ${INK};
`;

const Input = styled("textarea")`
  display: block;
  width: 100%;
  box-sizing: border-box;
  min-height: 80px;
  padding: 0;
  border: none;
  background: transparent;
  resize: none;
  font: 400 14px/20px ${OUTREACH_SANS};
  color: ${INK};
  outline: none;

  &::placeholder {
    color: ${MUTED};
  }

  &:disabled {
    color: ${MUTED};
  }
`;

const Bar = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 8px -8px 0;
  font-size: 13px;
  color: ${MUTED};
`;

const Note = styled("span")`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Tools = styled("div")`
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 0 0 auto;
`;

/* Icon-only tool, like the controls in Outreach's own composer. */
const IconTool = styled(MuiIconButton)<{ $on?: boolean }>`
  width: 36px;
  height: 36px;
  background: ${({ $on }) => ($on ? "#ecebff" : "transparent")};
  color: ${({ $on }) => ($on ? BRAND : "#A3A2BD")};

  &:hover:not(.Mui-disabled) {
    background: #eceef3;
    color: ${INK};
  }

  &.Mui-disabled {
    color: #c9cad6;
  }

  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
`;

/* Outreach's chat send button, measured: 36px round, #F6F6F7 with a hairline
   and #A3A2BD arrow until there is something to send. */
const SendBtn = styled(MuiIconButton)<{ $ready?: boolean }>`
  width: 36px;
  height: 36px;
  border: 1px solid ${({ $ready }) => ($ready ? BRAND : SOFT)};
  background: ${({ $ready }) => ($ready ? BRAND : "#f6f6f7")};
  color: ${({ $ready }) => ($ready ? "#fff" : "#a3a2bd")};
  margin-left: 6px;

  &:hover:not(.Mui-disabled) {
    background: ${({ $ready }) => ($ready ? BRAND_HOVER : "#eceef3")};
  }

  &.Mui-disabled {
    color: #a3a2bd;
    background: #f6f6f7;
    border-color: ${SOFT};
  }

  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
`;

/* Files waiting in the composer. */
const Pending = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 0 4px;
`;

const PendingChip = styled("div")`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 4px 0 6px;
  border-radius: 100px;
  background: #fff;
  box-shadow: ${PANEL_SHADOW};
  font: 400 13px/16px ${OUTREACH_SANS};
  color: ${INK};
  max-width: 260px;

  img {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    object-fit: cover;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const DropHint = styled("div")`
  position: absolute;
  inset: 0;
  border-radius: 8px;
  border: 2px dashed ${BRAND};
  background: rgba(94, 94, 175, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  font: 600 14px/20px ${OUTREACH_SANS};
  color: ${BRAND};
  pointer-events: none;
  z-index: 3;
`;

const GifPanel = styled("div")`
  width: min(360px, calc(100vw - 32px));
  display: flex;
  flex-direction: column;
  font-family: ${OUTREACH_SANS};
`;

const GifSearch = styled("input")`
  width: 100%;
  box-sizing: border-box;
  height: 36px;
  margin: 0;
  padding: 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 8px;
  font: 400 14px/20px ${OUTREACH_SANS};
  color: ${INK};
  outline: none;

  &:focus {
    border-color: ${BRAND};
  }
`;

const GifTabs = styled("div")`
  display: flex;
  gap: 4px;
  padding: 8px 12px 0;
`;

const GifTab = styled("button")<{ $on?: boolean }>`
  height: 28px;
  padding: 0 12px;
  border: none;
  border-radius: 100px;
  background: ${({ $on }) => ($on ? "#f2f2fe" : "transparent")};
  color: ${({ $on }) => ($on ? BRAND : MUTED)};
  font: 600 13px/1 ${OUTREACH_SANS};
  cursor: pointer;

  &:hover {
    background: ${SOFT};
  }
`;

const GifGrid = styled("div")`
  max-height: min(360px, calc(100vh - 220px));
  overflow-y: auto;
  padding: 8px 12px 0;
  column-count: 2;
  column-gap: 6px;

  button {
    display: block;
    width: 100%;
    margin: 0 0 6px;
    padding: 0;
    border: none;
    border-radius: 8px;
    overflow: hidden;
    background: ${SOFT};
    cursor: pointer;
    break-inside: avoid;
  }

  img {
    display: block;
    width: 100%;
    height: auto;
  }

  button:hover {
    opacity: 0.85;
  }
`;

const GifFoot = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px 10px;
  font: 400 11px/14px ${OUTREACH_SANS};
  color: ${MUTED};
`;

const LaterField = styled("input")`
  width: 100%;
  box-sizing: border-box;
  height: 36px;
  padding: 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 8px;
  background: #fff;
  font: 400 14px/20px ${OUTREACH_SANS};
  color: ${INK};

  &:focus {
    outline: none;
    border-color: ${BRAND};
  }
`;

/* ── icons: lifted from Outreach's own Emails table and chat ── */

/* Outreach's paper plane, lifted from their toolbar; a DM is sent, not mailed. */
const SendIcon = () => (
  <svg viewBox="0 0 24 24">
    <path fillRule="evenodd" clipRule="evenodd" d="M20.7294 3.31594C20.7221 3.30817 20.7147 3.30049 20.7071 3.29292C20.5313 3.11717 20.3064 3.02052 20.0766 3.00296C20.0141 2.99814 19.951 2.99918 19.8881 3.00627C19.8067 3.01538 19.7263 3.03445 19.6487 3.06348L2.85999 8.93954C1.57774 9.38833 1.50486 11.1743 2.74631 11.726L9.19342 14.5914C9.22314 14.6319 9.25629 14.6706 9.29286 14.7071C9.32944 14.7437 9.36814 14.7769 9.40858 14.8066L12.274 21.2537C12.8257 22.4951 14.6117 22.4223 15.0605 21.14L20.9366 4.35097C20.9645 4.27655 20.9831 4.19949 20.9926 4.12152C21.0283 3.83038 20.9342 3.53428 20.7294 3.31594ZM16.1943 6.3915L4.71105 10.4106L9.87851 12.7073L16.1943 6.3915ZM11.2927 14.1215L17.6085 7.80571L13.5894 19.289L11.2927 14.1215Z" />
  </svg>
);
const EyeIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12Z" />
    <path fillRule="evenodd" clipRule="evenodd" d="M1.31474 11.011C2.10361 9.20973 4.99768 4 12.0001 4C19.0025 4 21.8966 9.20972 22.6854 11.011C22.9632 11.6451 22.9632 12.3549 22.6854 12.989C21.8966 14.7903 19.0025 20 12.0001 20C4.99769 20 2.10361 14.7903 1.31474 12.989C1.03701 12.3549 1.03701 11.6451 1.31474 11.011ZM12 7C9.23858 7 7 9.23858 7 12C7 14.7614 9.23858 17 12 17C14.7614 17 17 14.7614 17 12C17 9.23858 14.7614 7 12 7Z" />
  </svg>
);
const ReplyIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M8.70711 6.70711C9.09763 6.31658 9.09763 5.68342 8.70711 5.29289C8.31658 4.90237 7.68342 4.90237 7.29289 5.29289L3.29289 9.29289C2.90237 9.68342 2.90237 10.3166 3.29289 10.7071L7.29289 14.7071C7.68342 15.0976 8.31658 15.0976 8.70711 14.7071C9.09763 14.3166 9.09763 13.6834 8.70711 13.2929L6.41421 11H12.8889C16.2337 11 19 13.9055 19 17.25V19C19 19.5523 19.4477 20 20 20C20.5523 20 21 19.5523 21 19V17.25C21 12.8625 17.3988 9 12.8889 9H6.41421L8.70711 6.70711Z" />
  </svg>
);
const BackIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" /></svg>
);
const EmojiIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" /></svg>
);
const ReplyLineIcon = ReplyIcon;
const ExternalIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M19 19H5V5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" /></svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
);
const MoreIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M6 10C4.89543 10 4 10.8954 4 12C4 13.1046 4.89543 14 6 14C7.10457 14 8 13.1046 8 12C8 10.8954 7.10457 10 6 10Z" />
    <path d="M12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10Z" />
    <path d="M18 10C16.8954 10 16 10.8954 16 12C16 13.1046 16.8954 14 18 14C19.1046 14 20 13.1046 20 12C20 10.8954 19.1046 10 18 10Z" />
  </svg>
);

/* Outreach's own Send later icon, lifted from their composer toggle. */
const ScheduleSendIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M13 6C13 5.44772 12.5523 5 12 5C11.4477 5 11 5.44772 11 6V12C11 12.3788 11.214 12.725 11.5528 12.8944L15.5528 14.8944C16.0468 15.1414 16.6474 14.9412 16.8944 14.4472C17.1414 13.9532 16.9412 13.3526 16.4472 13.1056L13 11.382V6Z" />
    <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM4 12C4 7.58172 7.58172 4 12 4C16.4183 4 20 7.58172 20 12C20 16.4183 16.4183 20 12 20C7.58172 20 4 16.4183 4 12Z" />
  </svg>
);
const AttachIcon = () => (
  <svg viewBox="0 0 24 24"><path fillRule="evenodd" clipRule="evenodd" d="M12 22C9.23858 22 7 19.7614 7 17L7 5C7 3.34315 8.34315 2 10 2C11.6569 2 13 3.34315 13 5L13 15.5C13 16.0523 12.5523 16.5 12 16.5C11.4477 16.5 11 16.0523 11 15.5L11 5C11 4.44772 10.5523 4 10 4C9.44772 4 9 4.44772 9 5L9 17C9 18.6569 10.3431 20 12 20C13.6569 20 15 18.6569 15 17L15 3C15 2.44772 15.4477 2 16 2C16.5523 2 17 2.44772 17 3L17 17C17 19.7614 14.7614 22 12 22Z" /></svg>
);
const ImageIcon = () => (
  <svg viewBox="0 0 24 24">
    <path fillRule="evenodd" clipRule="evenodd" d="M6 9C6 7.34315 7.34315 6 9 6C10.6569 6 12 7.34315 12 9C12 10.6569 10.6569 12 9 12C7.34315 12 6 10.6569 6 9ZM9 8C8.44772 8 8 8.44772 8 9C8 9.55229 8.44772 10 9 10C9.55229 10 10 9.55229 10 9C10 8.44772 9.55229 8 9 8Z" />
    <path fillRule="evenodd" clipRule="evenodd" d="M3 6C3 4.34315 4.34315 3 6 3H18C19.6569 3 21 4.34315 21 6V18C21 19.6569 19.6569 21 18 21H6C4.34315 21 3 19.6569 3 18V6ZM6 5C5.44772 5 5 5.44772 5 6V18C5 18.4362 5.27925 18.8071 5.66871 18.9438C5.70443 18.8906 5.74583 18.84 5.79289 18.7929L14.2929 10.2929C14.6834 9.90237 15.3166 9.90237 15.7071 10.2929L19 13.5858V6C19 5.44772 18.5523 5 18 5H6ZM19 16.4142L15 12.4142L8.41421 19H18C18.5523 19 19 18.5523 19 18V16.4142Z" />
  </svg>
);
const GifIcon = () => (
  <svg viewBox="0 0 24 24">
    <path fillRule="evenodd" clipRule="evenodd" d="M3 6C3 4.34315 4.34315 3 6 3H18C19.6569 3 21 4.34315 21 6V18C21 19.6569 19.6569 21 18 21H6C4.34315 21 3 19.6569 3 18V6ZM6 5C5.44772 5 5 5.44772 5 6V18C5 18.5523 5.44772 19 6 19H18C18.5523 19 19 18.5523 19 18V6C19 5.44772 18.5523 5 18 5H6Z" />
    <text x="12" y="15.2" textAnchor="middle" fontSize="7.5" fontWeight="700" fontFamily="Roboto Flex, Roboto, Arial, sans-serif" fill="currentColor" stroke="none">GIF</text>
  </svg>
);
const MemeIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-3-9.5a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zm5.5 1.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zM7.5 14h9c-.6 2.3-2.4 3.5-4.5 3.5S8.1 16.3 7.5 14z" /></svg>
);
const ArrowUpIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M5.70711 12.7071C5.31658 13.0976 4.68342 13.0976 4.29289 12.7071C3.90237 12.3166 3.90237 11.6834 4.29289 11.2929L11.2929 4.29289C11.6834 3.90237 12.3166 3.90237 12.7071 4.29289L19.7071 11.2929C20.0976 11.6834 20.0976 12.3166 19.7071 12.7071C19.3166 13.0976 18.6834 13.0976 18.2929 12.7071L13 7.41421L13 19C13 19.5523 12.5523 20 12 20C11.4477 20 11 19.5523 11 19L11 7.41421L5.70711 12.7071Z" /></svg>
);

interface GifPickerProps {
  onSelect: (gif: GifResult) => void;
}

const GIF_PAGE = 30;

/** Everything's picker on Outreach's chrome: GIPHY for All, GIPHY's meme search for Memes. */
const GifPicker: FC<GifPickerProps> = ({ onSelect }) => {
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState<"all" | "memes">("all");
  const [results, setResults] = useState<GifResult[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const offsetRef = useRef(0);
  const gridRef = useRef<HTMLDivElement>(null);

  const load = useCallback(
    async (append: boolean) => {
      setLoading(true);
      try {
        const offset = append ? offsetRef.current : 0;
        const page = await searchGiphy({ q: query.trim(), limit: GIF_PAGE, offset, memes: tab === "memes" });
        setResults((prev) => (append ? [...prev, ...page.results] : page.results));
        setHasMore(page.hasMore);
        offsetRef.current = offset + page.results.length;
      } catch {
        if (!append) setResults([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [query, tab]
  );

  useEffect(() => {
    const t = setTimeout(() => void load(false), 250);
    return () => clearTimeout(t);
  }, [load]);

  const onScroll = () => {
    const el = gridRef.current;
    if (!el || loading || !hasMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) void load(true);
  };

  return (
    <GifPanel>
      <div style={{ padding: "12px 12px 0" }}>
        <GifSearch value={query} placeholder="Search GIPHY" autoFocus onChange={(e) => setQuery(e.target.value)} />
      </div>
      <GifTabs>
        <GifTab type="button" $on={tab === "all"} onClick={() => setTab("all")}>
          All
        </GifTab>
        <GifTab type="button" $on={tab === "memes"} onClick={() => setTab("memes")}>
          Memes
        </GifTab>
      </GifTabs>
      <GifGrid ref={gridRef} onScroll={onScroll}>
        {results.map((gif) => (
          <button key={gif.id} type="button" title={gif.title} onClick={() => onSelect(gif)}>
            <img src={gif.preview} alt={gif.title} loading="lazy" width={gif.previewWidth} height={gif.previewHeight} />
          </button>
        ))}
        {!loading && results.length === 0 && <div style={{ color: MUTED, padding: "24px 0", textAlign: "center", columnSpan: "all" }}>No GIFs found</div>}
      </GifGrid>
      <GifFoot>{loading ? <CircularProgress size={14} thickness={5} style={{ color: BRAND }} /> : "Powered by GIPHY"}</GifFoot>
    </GifPanel>
  );
};

const REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🎉"];

const laterPresets = () => {
  const now = new Date();
  const hour = new Date(now.getTime() + 60 * 60_000);
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  tomorrow.setHours(9, 0, 0, 0);
  const monday = new Date(now);
  monday.setDate(now.getDate() + ((8 - now.getDay()) % 7 || 7));
  monday.setHours(9, 0, 0, 0);
  return [
    { label: "In 1 hour", at: hour },
    { label: "Tomorrow 9:00 AM", at: tomorrow },
    { label: "Next Monday 9:00 AM", at: monday },
  ];
};

const isLocalHost = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
const forceSkeleton = isLocalHost && typeof window !== "undefined" && new URLSearchParams(window.location.search).has("skeleton");
const SKELETON_ROWS = 12;

const daysAgo = (n: number) => new Date(Date.now() - n * 86400000).toISOString();

/** Sample rows so localhost can show the table without Outreach or a login. */
const PREVIEW_REPS = [
  { uid: "jamie", email: "Jamie Chen" },
  { uid: "sam", email: "Sam Ortiz" },
];

const PREVIEW_ROWS: InboxConversation[] = [
  {
    id: "preview-1",
    linkedin_conversation_id: "urn:li:msg:1",
    participant_name: "Alex Rivera",
    participant_vanity: "alex-rivera",
    participant_picture_url: "https://i.pravatar.cc/80?u=alex-rivera",
    last_message_text: "Thanks for sending that over — I'll take a look this week.",
    last_message_at: daysAgo(2),
    last_message_is_outbound: false,
    synced_by: "jamie",
    syncedByName: "Jamie Chen",
    syncedByEmail: "jamie@example.com",
    engagement: { sent: true, seen: true, replies: 2 },
  },
  {
    id: "preview-2",
    linkedin_conversation_id: "urn:li:msg:2",
    participant_name: "Priya Shah",
    participant_vanity: "priya-shah",
    participant_picture_url: "https://i.pravatar.cc/80?u=priya-shah",
    last_message_text: "Would Thursday afternoon work for a quick intro?",
    last_message_at: daysAgo(5),
    last_message_is_outbound: true,
    synced_by: "sam",
    syncedByName: "Sam Ortiz",
    syncedByEmail: "sam@example.com",
    engagement: { sent: true, seen: false, replies: 0 },
  },
  {
    id: "preview-3",
    linkedin_conversation_id: "urn:li:msg:3",
    participant_name: "Morgan Lee",
    participant_vanity: "morgan-lee",
    participant_picture_url: "https://i.pravatar.cc/80?u=morgan-lee",
    last_message_text: "Just bumped this in case it got buried.",
    last_message_at: daysAgo(12),
    last_message_is_outbound: true,
    synced_by: "jamie",
    syncedByName: "Jamie Chen",
    syncedByEmail: "jamie@example.com",
    engagement: { sent: true, seen: true, replies: 1 },
  },
];

const PREVIEW_MESSAGES: Record<string, InboxMessage[]> = {
  "preview-1": [
    { id: "m1", content: "Hi Alex — sending the one-pager we talked about. Happy to walk through it whenever is easy.", is_outbound: true, sent_at: daysAgo(8), sender_name: "Jamie Chen" },
    { id: "m2", content: "Got it, thanks Jamie.", is_outbound: false, sent_at: daysAgo(7), sender_name: "Alex Rivera", sender_picture_url: "https://i.pravatar.cc/80?u=alex-rivera" },
    { id: "m3", content: "Thanks for sending that over — I'll take a look this week.", is_outbound: false, sent_at: daysAgo(2), sender_name: "Alex Rivera", sender_picture_url: "https://i.pravatar.cc/80?u=alex-rivera" },
  ],
  "preview-2": [
    { id: "m4", content: "Would Thursday afternoon work for a quick intro?", is_outbound: true, sent_at: daysAgo(5), sender_name: "Sam Ortiz" },
    {
      id: "m4b",
      content: "",
      is_outbound: false,
      sent_at: daysAgo(4),
      sender_name: "Priya Shah",
      sender_picture_url: "https://i.pravatar.cc/80?u=priya-shah",
      media_urls: [
        { url: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=640", mediaType: "image/jpeg", type: "image", filename: "office.jpg" },
        { url: "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf", mediaType: "application/pdf", type: "file", filename: "one-pager.pdf", size: 13200 },
      ],
    },
  ],
  "preview-3": [
    { id: "m5", content: "Morgan, circling back on the note from last month.", is_outbound: true, sent_at: daysAgo(20), sender_name: "Jamie Chen" },
    { id: "m6", content: "Still interested — just buried. Can we pick this up next week?", is_outbound: false, sent_at: daysAgo(18), sender_name: "Morgan Lee" },
    { id: "m7", content: "Just bumped this in case it got buried.", is_outbound: true, sent_at: daysAgo(12), sender_name: "Jamie Chen" },
  ],
};

const initials = (name?: string | null) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("") || "?";

/* A post forwarded into the chat, as LinkedIn shows it: author, text, first visual. */
const PostCard = styled("div")`
  width: 360px;
  max-width: 100%;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: ${PANEL_SHADOW};
  background: #fff;
  font-size: 13px;
  line-height: 20px;

  a {
    color: inherit;
    text-decoration: none;
  }
`;

const PostHead = styled("a")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 12px 0;

  strong {
    display: block;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    display: block;
    color: ${MUTED};
    font-size: 12px;
    line-height: 16px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover strong {
    text-decoration: underline;
  }
`;

const PostText = styled("div")`
  padding: 8px 12px 0;
  white-space: pre-wrap;
  word-break: break-word;

  button {
    margin-left: 4px;
    padding: 0;
    border: none;
    background: none;
    color: ${MUTED};
    font: inherit;
    cursor: pointer;
  }

  button:hover {
    color: ${INK};
  }
`;

const PostImage = styled("a")`
  display: block;
  margin-top: 8px;
  background: #f8f8fa;

  img {
    display: block;
    width: 100%;
    height: auto;
    max-height: 360px;
    object-fit: contain;
  }
`;

const PostLink = styled("a")`
  display: block;
  padding: 10px 12px;
  border-top: 1px solid ${RULE};

  strong {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-weight: 600;
  }

  small {
    display: block;
    color: ${MUTED};
    font-size: 12px;
  }

  &:hover {
    background: ${WASH};
  }
`;

const PostFoot = styled("a")<{ flush?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: ${({ flush }) => (flush ? 0 : "8px")};
  padding: 8px 12px;
  border-top: 1px solid ${RULE};
  color: ${MUTED} !important;
  font-size: 12px;

  svg {
    width: 14px;
    height: 14px;
    fill: currentColor;
  }

  &:hover {
    color: ${INK} !important;
    background: ${WASH};
  }
`;

const Portrait = ({ src, name, size }: { src?: string | null; name: string; size?: number }) => (
  <Face src={src || undefined} alt={name} imgProps={{ referrerPolicy: "no-referrer" }} style={size ? { width: size, height: size, fontSize: size > 30 ? 13 : 11 } : undefined}>
    {initials(name)}
  </Face>
);

const LINKEDIN_POST = "linkedin/post";
const isPost = (item: InboxMedia) => item.mediaType === LINKEDIN_POST;

const kindOf = (item: InboxMedia) => {
  if (isPost(item)) return "post";
  const type = `${item.mediaType || ""} ${item.type || ""} ${item.filename || ""} ${item.url || ""}`.toLowerCase();
  if (type.includes("image/") || type.includes("gif") || item.type === "image" || item.type === "gif" || item.type === "giphy") return "image";
  if (type.includes("video/") || item.type === "video") return "video";
  if (type.includes("audio/") || item.type === "audio") return "audio";
  return "file";
};

const extOf = (item: InboxMedia) => {
  const fromName = (item.filename || "").split(".").pop();
  if (fromName && fromName !== item.filename && fromName.length <= 5) return fromName;
  const type = (item.mediaType || "").split("/")[1];
  if (type && type !== "*") return type.replace("jpeg", "jpg");
  return kindOf(item) === "file" ? "file" : kindOf(item);
};

const bytesOf = (n?: number | null) => {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const timeOf = (ms?: number | null) => {
  if (!ms) return "";
  const sec = Math.round(ms > 1000 ? ms / 1000 : ms);
  const m = Math.floor(sec / 60);
  const s = String(sec % 60).padStart(2, "0");
  return `${m}:${s}`;
};

const labelOf = (item: InboxMedia) => {
  if (item.filename) return item.filename;
  const kind = kindOf(item);
  if (kind === "image") return "Photo";
  if (kind === "video") return "Video";
  if (kind === "audio") return "Voice note";
  if (kind === "post") return "Shared a post";
  return "Attachment";
};

const FileCard = ({ item }: { item: InboxMedia }) => {
  const href = item.url || undefined;
  const inner = (
    <>
      <FileIcon>{extOf(item)}</FileIcon>
      <FileMeta>
        <strong>{labelOf(item)}</strong>
        <small>{[bytesOf(item.size), timeOf(item.duration)].filter(Boolean).join(" · ") || "Open"}</small>
      </FileMeta>
    </>
  );
  return href ? (
    <FileChip href={href} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
      {inner}
    </FileChip>
  ) : (
    <FileChip as="div">{inner}</FileChip>
  );
};

/* A DM column is narrow; clamp long posts back to a word boundary. */
const POST_CLAMP = 200;
const clampToWord = (text: string, limit: number) => {
  const cut = text.slice(0, limit);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd();
};

const SharedPost = ({ item }: { item: InboxMedia }) => {
  const [expanded, setExpanded] = useState(false);
  const [imageBroken, setImageBroken] = useState(false);
  const url = item.url || (item.postUrn ? `https://www.linkedin.com/feed/update/${item.postUrn}/` : "");
  const post = item.post;
  const open = { href: url, target: "_blank", rel: "noreferrer" };
  const foot = (flush: boolean) => (
    <PostFoot {...open} flush={flush}>
      <ExternalIcon />
      View post on LinkedIn
    </PostFoot>
  );
  // Stored before its preview could be read: still a post, not a deleted message.
  if (!post) return <PostCard>{foot(true)}</PostCard>;

  const name = post.authorName || "LinkedIn post";
  const text = post.text || "";
  const clamped = text.length > POST_CLAMP && !expanded;
  const image = post.imageUrl && !imageBroken ? post.imageUrl : null;
  return (
    <PostCard>
      <PostHead href={post.authorUrl || url} target="_blank" rel="noreferrer">
        <Portrait src={post.authorPictureUrl} name={name} size={36} />
        <span style={{ minWidth: 0 }}>
          <strong>{name}</strong>
          {post.authorHeadline && <small>{post.authorHeadline}</small>}
        </span>
      </PostHead>
      {text && (
        <PostText>
          {clamped ? `${clampToWord(text, POST_CLAMP)}…` : text}
          {clamped && (
            <button type="button" onClick={() => setExpanded(true)}>
              see more
            </button>
          )}
        </PostText>
      )}
      {image && (
        <PostImage {...open}>
          <img
            src={image}
            alt=""
            referrerPolicy="no-referrer"
            loading="lazy"
            onError={() => setImageBroken(true)}
            style={post.imageWidth && post.imageHeight ? { aspectRatio: `${post.imageWidth} / ${post.imageHeight}` } : undefined}
          />
        </PostImage>
      )}
      {(post.title || post.subtitle) && (
        <PostLink {...open}>
          {post.title && <strong>{post.title}</strong>}
          {post.subtitle && <small>{post.subtitle}</small>}
        </PostLink>
      )}
      {/* An image or link block already runs to the card edge. */}
      {foot(!!(image || post.title || post.subtitle))}
    </PostCard>
  );
};

const MediaItem = ({ item }: { item: InboxMedia }) => {
  const [broken, setBroken] = useState(false);
  const kind = kindOf(item);
  if (kind === "post") return <SharedPost item={item} />;
  const url = item.url || "";
  if (!url || broken) return <FileCard item={item} />;
  if (kind === "image") {
    return (
      <MediaFrame>
        <img src={url} alt={labelOf(item)} referrerPolicy="no-referrer" onError={() => setBroken(true)} onClick={() => window.open(url, "_blank", "noopener")} />
      </MediaFrame>
    );
  }
  if (kind === "video") {
    return (
      <MediaFrame>
        <video src={url} controls playsInline preload="metadata" onError={() => setBroken(true)} />
      </MediaFrame>
    );
  }
  if (kind === "audio") {
    return (
      <MediaFrame>
        <audio src={url} controls preload="metadata" onError={() => setBroken(true)} />
      </MediaFrame>
    );
  }
  return <FileCard item={item} />;
};

const MessageMedia = ({ items }: { items?: InboxMedia[] | null }) => {
  if (!items?.length) return null;
  return (
    <MediaStack>
      {items.map((item, i) => (
        <MediaItem key={`${item.url || item.assetUrn || item.filename || "m"}-${i}`} item={item} />
      ))}
    </MediaStack>
  );
};

/**
 * A message the sender deleted. LinkedIn keeps it in the thread with no body
 * and no media (messageBodyRenderFormat RECALLED); the extension stores it as
 * message_type "deleted", and rows stored before that are simply empty.
 */
const isDeleted = (m: InboxMessage) => m.message_type === "deleted" || (!m.content?.trim() && !m.media_urls?.length);
const DELETED_TEXT = "This message was deleted.";

/* Older syncs stored a forwarded post's render-content type ("FEED_UPDATE") as a reaction. */
const isReaction = (r: { emoji?: string | null }) => !!r?.emoji && !/^[A-Z0-9_]+$/.test(r.emoji);

const snippetOf = (m: InboxMessage) => {
  if (m.content?.trim()) return m.content.trim();
  const first = m.media_urls?.[0];
  return first ? labelOf(first) : DELETED_TEXT;
};

/*
 * What this frame last showed for a prospect: the conversation list and the
 * threads read for it. The frame is rebuilt every time the tab opens, so the
 * tab paints from here at once and refreshes behind, like the LinkedIn modal.
 * Kept across visits (this browser only, never shared) for the most recently
 * opened prospects.
 */
type ThreadCacheEntry = { messages: InboxMessage[]; canReply: boolean };
type ProspectCache = { rows: InboxConversation[]; threads: Record<string, ThreadCacheEntry> };
const CACHE_PREFIX = "boring.linkedinDms.v2:";
const CACHE_KEEP = 30;
const cacheKeyFor = (prospectId: string) => `${CACHE_PREFIX}${prospectId}`;
const readProspectCache = (prospectId: string): ProspectCache | null => {
  try {
    const raw = localStorage.getItem(cacheKeyFor(prospectId)) || sessionStorage.getItem(`boring.linkedinDms.v1:${prospectId}`);
    return raw ? (JSON.parse(raw) as ProspectCache) : null;
  } catch {
    return null;
  }
};
const writeProspectCache = (prospectId: string, cache: ProspectCache) => {
  try {
    localStorage.setItem(cacheKeyFor(prospectId), JSON.stringify({ ...cache, at: Date.now() }));
    // Only the most recent prospects are kept.
    const keys = Object.keys(localStorage).filter((k) => k.startsWith(CACHE_PREFIX));
    if (keys.length > CACHE_KEEP) {
      const at = (k: string) => {
        try {
          return Number(JSON.parse(localStorage.getItem(k) || "{}").at || 0);
        } catch {
          return 0;
        }
      };
      keys.sort((a, b) => at(a) - at(b)).slice(0, keys.length - CACHE_KEEP).forEach((k) => localStorage.removeItem(k));
    }
  } catch {
    // Full or blocked storage: the tab just loads from the network next time.
  }
};
// Threads read ahead once the list is in: a prospect rarely has more than a few.
const PREFETCH_THREADS = 5;

const shortDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");
const clock = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).toLowerCase();
const dayKey = (iso: string) => new Date(iso).toDateString();
const ago = (iso: string) => {
  const days = Math.floor((Date.now() - Date.parse(iso)) / 86400000);
  if (days < 1) return "today";
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? "" : "s"} ago`;
};

/** The person whose conversations to show, when the host is not Outreach (the HubSpot modal). */
export interface ConversationPerson {
  /** The CRM record id, sent as the prospect id the conversations are linked by. */
  id?: string;
  linkedinUrl?: string;
  name?: string;
  /** Keeps this frame's local cache apart from Outreach prospects with the same id. */
  cacheScope?: string;
}

interface Props {
  /** The tile opens the newest conversation directly; the tab shows the table; the inbox shows the whole team. */
  variant?: "tab" | "tile" | "inbox";
  /** Given, the Outreach SDK is not asked who the prospect is. */
  person?: ConversationPerson;
}

const LinkedInConversations: FC<Props> = ({ variant = "tab", person }) => {
  const isTile = variant === "tile";
  const isInbox = variant === "inbox";
  const [reps, setReps] = useState<{ uid: string; email: string | null }[]>([]);
  const [rep, setRep] = useState<string>("all");
  const [prospect, setProspect] = useState<{ id?: string; linkedinUrl?: string; name?: string; cacheScope?: string } | null>(null);
  const [rows, setRows] = useState<InboxConversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  // Threads already read for this prospect (from this frame, or the last one
  // in this browser tab), so opening one shows it at once.
  const threadCache = useRef<Map<string, ThreadCacheEntry>>(new Map());
  const activeIdRef = useRef<string | null>(null);
  activeIdRef.current = activeId;
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [canReply, setCanReply] = useState(false);
  const [draft, setDraft] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [replyTo, setReplyTo] = useState<InboxMessage | null>(null);
  const [reactFor, setReactFor] = useState<string | null>(null);
  const [laterAnchor, setLaterAnchor] = useState<HTMLElement | null>(null);
  // A row's ⋯ menu, and the conversation waiting on "Remove" to be confirmed.
  const [rowMenu, setRowMenu] = useState<{ anchor: HTMLElement; row: InboxConversation } | null>(null);
  const [removeRow, setRemoveRow] = useState<InboxConversation | null>(null);
  const [removing, setRemoving] = useState(false);
  const [threadLoading, setThreadLoading] = useState(false);
  const [pending, setPending] = useState<{ id: string; file?: File; gif?: GifResult; preview?: string; name: string }[]>([]);
  const [dragging, setDragging] = useState(false);
  const [gifAnchor, setGifAnchor] = useState<HTMLElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const addFiles = useCallback((files: FileList | File[] | null | undefined) => {
    if (!files) return;
    const list = Array.from(files).slice(0, 5);
    setPending((prev) =>
      [...prev, ...list.map((file) => ({ id: `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2, 6)}`, file, name: file.name, preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined }))].slice(0, 5)
    );
  }, []);

  const addGif = useCallback((gif: GifResult) => {
    setPending((prev) => [...prev, { id: `gif-${gif.id}`, gif, name: gif.title || "GIF", preview: gif.preview }].slice(0, 5));
    setGifAnchor(null);
  }, []);

  const removePending = (id: string) => setPending((prev) => prev.filter((p) => p.id !== id));
  const [laterAt, setLaterAt] = useState("");
  const threadRef = useRef<HTMLDivElement>(null);

  const pinThreadToLatest = () => {
    const el = threadRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "light");
    const prevBody = document.body.style.background;
    const prevHtml = document.documentElement.style.background;
    document.body.style.background = CANVAS;
    document.documentElement.style.background = CANVAS;
    return () => {
      document.body.style.background = prevBody;
      document.documentElement.style.background = prevHtml;
    };
  }, []);

  // Borrowing the extension's session is the slowest step before any read, so
  // it starts with the frame instead of after Outreach says which prospect.
  useEffect(() => {
    if (!isLocalHost) ensureSignedInFromExtension().catch(() => undefined);
  }, []);

  useEffect(() => {
    if (person) {
      // HubSpot (or any host that is not Outreach) names the person itself.
      setProspect({ id: person.id, linkedinUrl: person.linkedinUrl, name: person.name, cacheScope: person.cacheScope });
      return;
    }
    if (isInbox) {
      // The inbox is not about one prospect; the SDK still sizes the frame.
      extensibilitySdk.init().catch(() => undefined);
      return;
    }
    extensibilitySdk
      .init()
      .then((ctx: any) =>
        setProspect({
          id: ctx?.prospect?.id ? String(ctx.prospect.id) : undefined,
          linkedinUrl: ctx?.prospect?.linkedInUrl,
          // The name is the signal that finds conversations stored under an
          // opaque member id rather than a readable profile slug.
          name: ctx?.prospect?.name || [ctx?.prospect?.firstName, ctx?.prospect?.lastName].filter(Boolean).join(" ") || undefined,
        })
      )
      .catch(() => setProspect({}));
  }, [isInbox, person]);

  useEffect(() => {
    if (!isInbox) return;
    setLoading(true);
    // The frame has no session of its own (see ensureSignedInFromExtension).
    ensureSignedInFromExtension()
      .then(() => getLinkedInInbox(rep !== "all" ? { syncedBy: rep } : {}))
      .then((r) => {
        setRows(r.conversations);
        setReps(r.reps);
      })
      .catch((error) => {
        if (isPlanRequiredError(error)) return setNote(PLAN_NOTE);
        if (isLocalHost) {
          setRows(rep === "all" ? PREVIEW_ROWS : PREVIEW_ROWS.filter((r) => r.synced_by === rep));
          setReps(PREVIEW_REPS);
          setNote(null);
          return;
        }
        setNote("Could not load the inbox.");
      })
      .finally(() => setLoading(false));
  }, [isInbox, rep]);

  useEffect(() => {
    if (!prospect || isInbox) return;
    const prospectId = prospect.id ? `${prospect.cacheScope ? `${prospect.cacheScope}:` : ""}${prospect.id}` : null;
    const cached = prospectId ? readProspectCache(prospectId) : null;
    threadCache.current = new Map(Object.entries(cached?.threads || {}));
    if (cached?.rows?.length) {
      setRows(cached.rows);
      setLoading(false);
      if (isTile) setActiveId(cached.rows[0].id);
    }
    const save = (rowsToSave: InboxConversation[]) => {
      if (prospectId) writeProspectCache(prospectId, { rows: rowsToSave, threads: Object.fromEntries(threadCache.current) });
    };
    // Conversations are stored under the member urn LinkedIn hands the
    // syncer, which is often an opaque id rather than the vanity in the
    // Outreach URL. The extension resolves the URL with the rep's session so
    // the lookup can match on the urn as well; that runs alongside the first
    // read (by id, URL and name), which is usually enough, and only a miss
    // waits for it and asks again. The frame first borrows the extension's
    // session, since it has none of its own.
    const member = prospect.linkedinUrl ? resolveLinkedInUrl(prospect.linkedinUrl) : Promise.resolve(null);
    const query = (m: { urn?: string; name?: string | null } | null) =>
      getLinkedInConversations({
        prospectId: prospect.id,
        linkedinUrl: prospect.linkedinUrl,
        name: prospect.name || m?.name || undefined,
        participantUrn: m?.urn,
      });
    const show = ({ conversations: r, threads }: { conversations: InboxConversation[]; threads: Record<string, ThreadCacheEntry> }) => {
      // The newest threads come with the list, so opening one needs no wait.
      for (const [id, t] of Object.entries(threads)) threadCache.current.set(id, t);
      setRows(r);
      if (isTile && r.length && !activeIdRef.current) setActiveId(r[0].id);
      const open = activeIdRef.current && threads[activeIdRef.current];
      if (open) {
        setMessages(open.messages);
        setCanReply(open.canReply);
        setThreadLoading(false);
      }
      save(r);
      // Any of the first few the list did not bring, read ahead as before.
      r.slice(0, PREFETCH_THREADS)
        .filter((row) => !threads[row.id])
        .forEach((row) => {
          getLinkedInThread(row.id)
            .then((t) => {
              threadCache.current.set(row.id, { messages: t.messages, canReply: t.canReply });
              save(r);
              if (activeIdRef.current === row.id) {
                setMessages(t.messages);
                setCanReply(t.canReply);
                setThreadLoading(false);
              }
            })
            .catch(() => {
              // A read ahead that fails just leaves that thread to load on click.
            });
        });
    };
    ensureSignedInFromExtension()
      .then(() => query(null))
      .then(async (first) => {
        if (first.conversations.length) return show(first);
        const m = await member;
        return show(m?.urn ? await query(m) : first);
      })
      .catch((error) => {
        if (isPlanRequiredError(error)) return setNote(PLAN_NOTE);
        if (isLocalHost) {
          setRows(PREVIEW_ROWS);
          setNote(null);
          if (isTile) setActiveId(PREVIEW_ROWS[0].id);
          return;
        }
        setNote("Could not load conversations.");
      })
      .finally(() => setLoading(false));
  }, [prospect, isInbox, isTile]);

  useEffect(() => {
    if (!activeId) return;
    setMessages([]);
    setReplyTo(null);
    setReactFor(null);
    setLaterAnchor(null);
    if (isLocalHost && PREVIEW_MESSAGES[activeId]) {
      setMessages(PREVIEW_MESSAGES[activeId]);
      setCanReply(true);
      return;
    }
    // Read ahead when the list came in: shown now, refreshed behind.
    const cachedThread = threadCache.current.get(activeId);
    if (cachedThread) {
      setMessages(cachedThread.messages);
      setCanReply(cachedThread.canReply);
    } else {
      setThreadLoading(true);
    }
    const id = activeId;
    getLinkedInThread(id)
      .then((r) => {
        threadCache.current.set(id, { messages: r.messages, canReply: r.canReply });
        if (activeIdRef.current !== id) return;
        setMessages(r.messages);
        setCanReply(r.canReply);
      })
      .catch(() => {
        if (!cachedThread && activeIdRef.current === id) setMessages([]);
      })
      .finally(() => {
        if (activeIdRef.current === id) setThreadLoading(false);
      });
  }, [activeId]);

  useLayoutEffect(() => {
    if (!activeId) return;
    pinThreadToLatest();
    const el = threadRef.current;
    if (!el) return;
    const frame = requestAnimationFrame(pinThreadToLatest);
    const onLoad = () => pinThreadToLatest();
    el.querySelectorAll("img").forEach((img) => {
      if (!img.complete) img.addEventListener("load", onLoad);
    });
    return () => {
      cancelAnimationFrame(frame);
      el.querySelectorAll("img").forEach((img) => img.removeEventListener("load", onLoad));
    };
  }, [activeId, messages]);

  const active = useMemo(() => rows.find((r) => r.id === activeId) || null, [rows, activeId]);

  const profileUrl = active
    ? active.participant_vanity
      ? `https://www.linkedin.com/in/${active.participant_vanity}/`
      : prospect?.linkedinUrl || ""
    : "";

  const queue = useCallback(
    async (input: { text?: string; emoji?: string; messageId?: string; scheduledAt?: string; reply?: InboxMessage | null; attachments?: LinkedInAttachment[] }) => {
      if (!active) return { queued: false, reason: "No conversation" };
      return sendLinkedInReply({
        conversationId: active.linkedin_conversation_id || active.id,
        linkedinUrl: profileUrl,
        text: input.text,
        attachments: input.attachments,
        emoji: input.emoji,
        messageId: input.messageId,
        prospectId: prospect?.id,
        scheduledAt: input.scheduledAt,
        replyTo: input.reply
          ? {
              messageUrn: input.reply.linkedin_message_id || input.reply.id,
              senderUrn: input.reply.sender_linkedin_urn || undefined,
              senderName: input.reply.sender_name || undefined,
              text: input.reply.content || undefined,
              sentAt: input.reply.sent_at,
            }
          : undefined,
      });
    },
    [active, profileUrl, prospect]
  );

  const send = useCallback(async (when?: Date) => {
    const text = draft.trim();
    if ((!text && pending.length === 0) || !active) return;
    setSending(true);
    setLaterAnchor(null);
    try {
      const scheduledAt = when && when.getTime() > Date.now() ? when.toISOString() : undefined;
      // Files go to storage first; GIFs are already links.
      const attachments: LinkedInAttachment[] = [];
      for (const item of pending) {
        if (item.file) attachments.push(await uploadLinkedInAttachment(item.file));
        else if (item.gif) attachments.push({ url: item.gif.full || item.gif.preview, name: `${(item.gif.title || "giphy").replace(/[^\w-]+/g, "-").slice(0, 60) || "giphy"}.gif`, mediaType: "image/gif", size: 0 });
      }
      const r = await queue({ text, scheduledAt, reply: replyTo, attachments });
      if (r.queued) {
        setDraft("");
        setReplyTo(null);
        setPending([]);
        if (scheduledAt) {
          setMessages((prev) => [
            ...prev,
            {
              id: `sched-${Date.now()}`,
              content: text,
              is_outbound: true,
              sent_at: scheduledAt,
              sender_name: "You",
              is_scheduled: true,
              scheduled_at: scheduledAt,
              reply_to: replyTo ? { text: replyTo.content || "", senderName: replyTo.sender_name || "", messageUrn: replyTo.linkedin_message_id || replyTo.id } : null,
            },
          ]);
          setNote(`Scheduled for ${new Date(scheduledAt).toLocaleString()}. It sends from your own LinkedIn session.`);
        } else {
          setNote("Queued. It sends from your own LinkedIn session shortly.");
        }
      } else {
        setNote(r.reason || "Could not queue that reply.");
      }
    } catch (e: any) {
      setNote(e?.message || "Could not queue that reply.");
    } finally {
      setSending(false);
    }
  }, [draft, active, queue, replyTo, pending]);

  const react = useCallback(
    async (message: InboxMessage, emoji: string) => {
      if (!active) return;
      setReactFor(null);
      try {
        const r = await queue({ emoji, messageId: message.linkedin_message_id || message.id });
        if (r.queued) {
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== message.id) return m;
              const existing = m.reactions || [];
              const hit = existing.find((x) => x.emoji === emoji);
              const reactions = hit
                ? existing.map((x) => (x.emoji === emoji ? { ...x, count: x.count + 1, viewerReacted: true } : x))
                : [...existing, { emoji, count: 1, viewerReacted: true }];
              return { ...m, reactions };
            })
          );
          setNote("Reaction queued. It sends from your own LinkedIn session shortly.");
        } else {
          setNote(r.reason || "Could not queue that reaction.");
        }
      } catch (e: any) {
        setNote(e?.message || "Could not queue that reaction.");
      }
    },
    [active, queue]
  );

  const showSkeleton = forceSkeleton || loading;

  const columns = isInbox ? 6 : 5;
  const headers = (
    <TableHead>
      <TableRow>
        {isInbox && <HeadCell>Prospect</HeadCell>}
        <HeadCell>Contacted by</HeadCell>
        <HeadCell>Content</HeadCell>
        <HeadCell>Engagement</HeadCell>
        <HeadCell>Date sent</HeadCell>
        <HeadCell />
      </TableRow>
    </TableHead>
  );

  /* ── thread ── */
  if (activeId && active) {
    const repName = active.syncedByName || active.syncedByEmail || "Teammate";
    const them = active.participant_name || prospect?.name || "Prospect";
    let lastDay = "";
    return (
      <ThemeProvider theme={outreachTheme}>
      <Page>
        <ThreadPanel>
          {!isTile && (
            <ThreadHead>
              <Tip title="Back to conversations">
                <RoundIcon aria-label="Back to conversations" onClick={() => setActiveId(null)}>
                  <BackIcon />
                </RoundIcon>
              </Tip>
              <Name>{them}</Name>
            </ThreadHead>
          )}
          <Scroll ref={threadRef}>
            {threadLoading ? (
              <Centered>
                <CircularProgress size={28} thickness={4} style={{ color: BRAND }} />
              </Centered>
            ) : messages.length === 0 ? (
              <Empty>No messages stored yet. Open this conversation on LinkedIn once and it will sync.</Empty>
            ) : (
              messages.map((m) => {
                const key = dayKey(m.sent_at);
                const rule = key !== lastDay;
                lastDay = key;
                const who = m.is_outbound ? repName : m.sender_name || them;
                const pic = m.is_outbound ? m.sender_picture_url : m.sender_picture_url || active.participant_picture_url;
                return (
                  <React.Fragment key={m.id}>
                    {rule && (
                      <DayRule>
                        {shortDate(m.sent_at)} <span>({ago(m.sent_at)})</span>
                      </DayRule>
                    )}
                    <Message>
                      <Portrait src={pic} name={who} size={36} />
                      <div>
                        <Sender>
                          {who}{" "}
                          <small>
                            • {m.is_scheduled ? `sends ${clock(m.scheduled_at || m.sent_at)}` : clock(m.sent_at)}
                            {m.is_scheduled ? " · scheduled" : ""}
                          </small>
                        </Sender>
                        {m.reply_to?.text && (
                          <Quote>
                            {m.reply_to.senderName ? `${m.reply_to.senderName}: ` : ""}
                            {m.reply_to.text}
                          </Quote>
                        )}
                        {m.content?.trim() ? <Body>{m.content}</Body> : null}
                        <MessageMedia items={m.media_urls} />
                        {isDeleted(m) ? <Deleted>{DELETED_TEXT}</Deleted> : null}
                        {!!m.reactions?.some(isReaction) && (
                          <Chips>
                            {m.reactions.filter(isReaction).map((r) => (
                              <ReactionChip key={r.emoji} on={r.viewerReacted} label={`${r.emoji} ${r.count}`} />
                            ))}
                          </Chips>
                        )}
                      </div>
                      {!m.is_scheduled && (
                        <MessageActions className="msg-actions">
                          <Tip title="React">
                            <SmallIcon aria-label="React" onClick={() => setReactFor(reactFor === m.id ? null : m.id)}>
                              <EmojiIcon />
                            </SmallIcon>
                          </Tip>
                          <Tip title="Reply">
                            <SmallIcon
                              aria-label="Reply"
                              onClick={() => {
                                setReplyTo(m);
                                setReactFor(null);
                              }}
                            >
                              <ReplyLineIcon />
                            </SmallIcon>
                          </Tip>
                        </MessageActions>
                      )}
                      {reactFor === m.id && (
                        <EmojiSheet>
                          {REACTIONS.map((emoji) => (
                            <EmojiBtn key={emoji} type="button" onClick={() => react(m, emoji)}>
                              {emoji}
                            </EmojiBtn>
                          ))}
                        </EmojiSheet>
                      )}
                    </Message>
                  </React.Fragment>
                );
              })
            )}
          </Scroll>
          <Composer
            onDragOver={(e) => {
              e.preventDefault();
              if (canReply) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              if (canReply) addFiles(e.dataTransfer.files);
            }}
          >
            {dragging && <DropHint>Drop to attach</DropHint>}
            <input ref={fileInputRef} type="file" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            <input ref={imageInputRef} type="file" accept="image/*,video/*" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            {replyTo && (
              <ReplyBar>
                <div>
                  Replying to {replyTo.sender_name || (replyTo.is_outbound ? repName : them)}
                  <div style={{ color: MUTED, marginTop: 2 }}>{snippetOf(replyTo).slice(0, 140)}</div>
                </div>
                <SmallIcon aria-label="Cancel reply" onClick={() => setReplyTo(null)}>
                  <CloseIcon />
                </SmallIcon>
              </ReplyBar>
            )}
            <Input
              value={draft}
              disabled={!canReply}
              placeholder={canReply ? "Write a message…" : `Only ${repName} can reply here`}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && canReply && draft.trim()) {
                  e.preventDefault();
                  void send();
                }
              }}
            />
            {pending.length > 0 && (
              <Pending>
                {pending.map((item) => (
                  <PendingChip key={item.id}>
                    {item.preview ? <img src={item.preview} alt="" /> : <FileIcon style={{ width: 28, height: 28, flexBasis: 28 }}>{(item.name.split(".").pop() || "file").slice(0, 4)}</FileIcon>}
                    <span>{item.name}</span>
                    <SmallIcon aria-label="Remove" onClick={() => removePending(item.id)}>
                      <CloseIcon />
                    </SmallIcon>
                  </PendingChip>
                ))}
              </Pending>
            )}
            <Bar>
              <Tools style={{ marginRight: "auto", gap: 0 }}>
                <Tip title="Add attachment">
                  <span>
                    <IconTool disabled={!canReply} aria-label="Add attachment" onClick={() => fileInputRef.current?.click()}>
                      <AttachIcon />
                    </IconTool>
                  </span>
                </Tip>
                <Tip title="Add image">
                  <span>
                    <IconTool disabled={!canReply} aria-label="Add image" onClick={() => imageInputRef.current?.click()}>
                      <ImageIcon />
                    </IconTool>
                  </span>
                </Tip>
                <Tip title="GIF">
                  <span>
                    <IconTool disabled={!canReply} $on={!!gifAnchor} aria-label="GIF" onClick={(e) => setGifAnchor(e.currentTarget)}>
                      <GifIcon />
                    </IconTool>
                  </span>
                </Tip>
              </Tools>
              <Note>{note || ""}</Note>
              <Tools>
                <Tip title="Send later">
                  <span>
                    <IconTool
                      $on={!!laterAnchor}
                      disabled={!canReply || (!draft.trim() && pending.length === 0) || sending}
                      aria-label="Send later"
                      onClick={(e) => setLaterAnchor(e.currentTarget)}
                    >
                      <ScheduleSendIcon />
                    </IconTool>
                  </span>
                </Tip>
                <Tip title="Send">
                  <span>
                    <SendBtn
                      $ready={!!(canReply && (draft.trim() || pending.length > 0) && !sending)}
                      disabled={!canReply || (!draft.trim() && pending.length === 0) || sending}
                      aria-label="Send"
                      onClick={() => void send()}
                    >
                      {sending ? <CircularProgress size={16} thickness={5} style={{ color: "#a3a2bd" }} /> : <ArrowUpIcon />}
                    </SendBtn>
                  </span>
                </Tip>
              </Tools>
            </Bar>
            <Popover
              open={!!gifAnchor}
              anchorEl={gifAnchor}
              onClose={() => setGifAnchor(null)}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              transformOrigin={{ vertical: "bottom", horizontal: "left" }}
              marginThreshold={8}
              PaperProps={{ style: { borderRadius: 8, boxShadow: `${PANEL_SHADOW}, 0 8px 24px rgba(69, 66, 98, 0.12)` } }}
            >
              {gifAnchor && <GifPicker onSelect={addGif} />}
            </Popover>
            <Menu
              anchorEl={laterAnchor}
              open={!!laterAnchor}
              onClose={() => setLaterAnchor(null)}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "bottom", horizontal: "right" }}
              PaperProps={{ style: { borderRadius: 8, boxShadow: `${PANEL_SHADOW}, 0 8px 24px rgba(69, 66, 98, 0.12)`, minWidth: 260, fontFamily: OUTREACH_SANS } }}
            >
              {laterPresets().map((p) => (
                <MenuItem key={p.label} style={{ font: `400 14px/20px ${OUTREACH_SANS}`, color: INK }} onClick={() => void send(p.at)}>
                  {p.label}
                </MenuItem>
              ))}
              <div style={{ padding: "8px 16px 12px" }}>
                <LaterField type="datetime-local" value={laterAt} onChange={(e) => setLaterAt(e.target.value)} />
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                  <PrimaryButton
                    disabled={!laterAt}
                    onClick={() => {
                      if (!laterAt) return;
                      void send(new Date(laterAt));
                    }}
                  >
                    Schedule
                  </PrimaryButton>
                </div>
              </div>
            </Menu>
          </Composer>
        </ThreadPanel>
      </Page>
      </ThemeProvider>
    );
  }

  /* ── table ── */
  const filter = isInbox && (
    <Filter>
      <Select
        value={rep}
        onChange={(e: any) => setRep(e?.target?.value ?? e ?? "all")}
        options={[{ value: "all", label: "Everyone" }, ...reps.map((r) => ({ value: r.uid, label: r.email || "Teammate" }))]}
      />
    </Filter>
  );

  return (
    <ThemeProvider theme={outreachTheme}>
    <Page>
      {filter}
      <TablePanel>
        <Scrollable>
          <Table stickyHeader size="medium" style={{ fontFamily: OUTREACH_SANS, tableLayout: "fixed" }}>
            {/* The message gets whatever is left once the fixed columns are placed. */}
            <colgroup>
              {isInbox && <col style={{ width: "22%" }} />}
              <col style={{ width: isInbox ? "20%" : "26%" }} />
              <col />
              <col style={{ width: 150 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 56 }} />
            </colgroup>
            {headers}
            <TableBody>
              {showSkeleton ? (
                // Outreach's table placeholder, measured off the prospect
                // messages tab while loading: one 12px text bone per column,
                // no avatars, no rules, 52px rows with 16px cell padding.
                Array.from({ length: SKELETON_ROWS }, (_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {Array.from({ length: columns }, (_, c) => (
                      <Cell key={c} style={{ height: 52, padding: "16px 12px", borderBottom: "none" }}>
                        <Bone variant="text" width={c === columns - 1 ? 44 : "100%"} animation="pulse" style={{ fontSize: 12 }} />
                      </Cell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <Cell colSpan={columns} style={{ height: "auto", borderBottom: "none" }}>
                    <NoResults>
                      <SearchIcon />
                      {note || "No results"}
                    </NoResults>
                  </Cell>
                </TableRow>
              ) : (
                rows.map((r) => {
                  const who = r.syncedByName || r.syncedByEmail || "Teammate";
                  const e = r.engagement || { sent: r.last_message_is_outbound === true, seen: false, replies: 0 };
                  const text = r.last_message_text || "";
                  return (
                    <ClickRow key={r.id} hover={false} onClick={() => setActiveId(r.id)}>
                      {isInbox && (
                        <Cell>
                          <Person>
                            <Portrait src={r.participant_picture_url} name={r.participant_name || "LinkedIn member"} />
                            <Name>{r.participant_name || "LinkedIn member"}</Name>
                          </Person>
                        </Cell>
                      )}
                      <Cell>
                        <Person>
                          <Portrait name={who} />
                          <Name>{who}</Name>
                        </Person>
                      </Cell>
                      <Cell>
                        <Snippet>{text || "LinkedIn message"}</Snippet>
                      </Cell>
                      <Cell>
                        <Engagement>
                          <Tip title="Sent"><Icon on={e.sent}><SendIcon /></Icon></Tip>
                          <Tip title="Seen"><Icon on={e.seen}><EyeIcon /></Icon></Tip>
                          <Tip title={e.replies ? `${e.replies} ${e.replies === 1 ? "reply" : "replies"}` : "No replies"}>
                            <Icon on={e.replies > 0}>
                              <ReplyIcon />
                              {e.replies > 0 && <Count>{e.replies}</Count>}
                            </Icon>
                          </Tip>
                        </Engagement>
                      </Cell>
                      <Cell>
                        <DateCell>{shortDate(r.last_message_at)}</DateCell>
                      </Cell>
                      <Cell align="right">
                        <RoundIcon
                          aria-label="More actions"
                          style={{ color: INK, width: 24, height: 24, padding: 0 }}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setRowMenu({ anchor: ev.currentTarget, row: r });
                          }}
                        >
                          <MoreIcon />
                        </RoundIcon>
                      </Cell>
                    </ClickRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Scrollable>
      </TablePanel>
      <Menu
        anchorEl={rowMenu?.anchor || null}
        open={!!rowMenu}
        onClose={() => setRowMenu(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ style: { borderRadius: 8, boxShadow: `${PANEL_SHADOW}, 0 8px 24px rgba(69, 66, 98, 0.12)`, minWidth: 220, fontFamily: OUTREACH_SANS } }}
      >
        <MenuItem
          style={{ font: `400 14px/20px ${OUTREACH_SANS}`, color: INK }}
          onClick={() => {
            if (rowMenu) setActiveId(rowMenu.row.id);
            setRowMenu(null);
          }}
        >
          Open conversation
        </MenuItem>
        <MenuItem
          style={{ font: `400 14px/20px ${OUTREACH_SANS}`, color: DANGER }}
          onClick={() => {
            if (rowMenu) setRemoveRow(rowMenu.row);
            setRowMenu(null);
          }}
        >
          Remove conversation
        </MenuItem>
      </Menu>
      <Dialog
        open={!!removeRow}
        onClose={() => !removing && setRemoveRow(null)}
        PaperProps={{ style: { borderRadius: 8, padding: 24, width: 420, maxWidth: "calc(100vw - 32px)", background: "#FEFDFC", fontFamily: OUTREACH_SANS } }}
      >
        <div style={{ font: `600 20px/28px ${OUTREACH_SANS}`, color: INK, marginBottom: 8 }}>Remove this conversation?</div>
        <div style={{ font: `400 14px/20px ${OUTREACH_SANS}`, color: MUTED }}>
          It leaves the LinkedIn DMs tab and the inbox for everyone on your team, and stays out when LinkedIn syncs again. The conversation itself stays in LinkedIn.
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
          <SecondaryButton disabled={removing} onClick={() => setRemoveRow(null)}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            disabled={removing}
            style={{ background: removing ? undefined : DANGER, minWidth: 96 }}
            onClick={async () => {
              if (!removeRow) return;
              const target = removeRow;
              setRemoving(true);
              try {
                const ok = await removeLinkedInConversation(target.id);
                if (!ok) throw new Error("not removed");
                const next = rows.filter((row) => row.id !== target.id);
                threadCache.current.delete(target.id);
                setRows(next);
                if (activeIdRef.current === target.id) setActiveId(null);
                if (prospect?.id) writeProspectCache(String(prospect.id), { rows: next, threads: Object.fromEntries(threadCache.current) });
                setRemoveRow(null);
              } catch {
                setNote("Could not remove the conversation. Try again.");
                setRemoveRow(null);
              } finally {
                setRemoving(false);
              }
            }}
          >
            {removing ? <CircularProgress size={16} style={{ color: "#fff" }} /> : "Remove"}
          </PrimaryButton>
        </div>
      </Dialog>
    </Page>
    </ThemeProvider>
  );
};

export default LinkedInConversations;
